import { Box } from '@chronosphereio/chrono-ui';
import { AbsoluteTimeRange } from '@chronosphereio/core';
import { utcToZonedTime } from 'date-fns-tz';
import { HeatmapChart as EChartsHeatmapChart } from 'echarts/charts';
import {
  DatasetComponent,
  DataZoomComponent,
  GridComponent,
  TitleComponent,
  TooltipComponent,
  VisualMapComponent,
} from 'echarts/components';
import { EChartsCoreOption, use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { merge } from 'lodash';
import { useMemo, useRef } from 'react';
import { ChartApi, EChart, getFormattedAxisLabel, useChartsContext, YAxisLabel } from '@/components';
import { EVENT_TIMELINE_HEIGHT, EventTimeline } from '@/components/EventTimeline';
import {
  ContentDimensions,
  EventItem,
  formatValue,
  HeatMapYAxisOptions,
  HeatmapColorOptions,
  HeatmapLegendOptions,
} from '@/core';
import { useThrottledResizing } from '../../../core/utils/resizing';
import { getXAxisMinInterval } from '../time-series-chart/utils/model';
import { ColorScales, CustomColorScale, scaleToRgbString } from './color-scales';
import { HeatMapCanvas, HeatmapCanvasColorOptions } from './HeatMapCanvas';
import { HeatMapInteractivePane } from './HeatMapInteractivePane';
import { DEFAULT_UNIT, formatBucketBoundary, HeatMapChartData } from './heat-map-model';
import { useEChartsGridCoordinates } from './useEChartsGridCoordinates';

// eslint-disable-next-line react-hooks/rules-of-hooks
use([
  EChartsHeatmapChart,
  GridComponent,
  DatasetComponent,
  TitleComponent,
  TooltipComponent,
  CanvasRenderer,
  VisualMapComponent,
  DataZoomComponent,
]);

const MIN_CHART_HEIGHT = 100;

const PADDING = {
  right: {
    default: 10,
    legend: 46,
  },
  bottom: {
    default: 0,
    legend: 32,
  },
};

const CONSTANT_OPTIONS: EChartsCoreOption = {
  dataZoom: {
    type: 'inside',
    filterMode: 'none',
    xAxisIndex: 0,
  },
  toolbox: {
    feature: {
      dataZoom: {
        icon: null, // https://stackoverflow.com/a/67684076/17575201
        yAxisIndex: false,
      },
    },
  },
};

const NO_DATA_YAXIS_OPTIONS = {
  // Switching to a value axis, so we can force 0 to be at the bottom.
  type: 'value',
  // Min of 0 to show zero.
  min: 0,
  // Max of 1 because that's what echarts apparently falls back to if you try to set it to zero.
  // Explicitly setting it to ensure consistent behavior if something there changes.
  max: 1,
  // Set interval of 1, so echarts only tries to render 0 and 1 on the y-axis
  interval: 1,
  axisLabel: {
    // Hide the maxLabel, so the max of 1 isn't shown.
    showMaxLabel: false,
  },
};

const EMPTY_EVENTS: EventItem[] = [];

export interface HeatMapChartProps extends ContentDimensions {
  /**
   * heatmap chart data
   */
  data: HeatMapChartData;
  /**
   * time zone used for formatting the time axis
   */
  timeZone: string;
  /**
   * color-related configuration options
   */
  colorOptions: HeatmapColorOptions;
  /**
   * legend-related configuration options
   */
  legendOptions: HeatmapLegendOptions;
  /**
   * y-axis configurations
   */
  yAxis: HeatMapYAxisOptions;
  /**
   * callback for drag to zoom
   */
  onDataZoom: (timeRange: AbsoluteTimeRange) => void;
  /**
   * Optional prop that may be used to provide a custom color scale and getColor function in programmatic use cases
   */
  customColorScale?: CustomColorScale;
  /**
   * Events to render on the chart
   */
  events?: EventItem[];
  /**
   * Callback when an event is clicked
   */
  onEventClick?: (event: EventItem) => void;
  /**
   * Unique panel key for tooltip management (should be unique per panel instance)
   */
  panelKey?: string;
}

/**
 * Heatmap chart
 */
export function HeatMapChart({
  data,
  timeZone,
  width,
  height,
  colorOptions: initColorOptions,
  legendOptions,
  yAxis,
  onDataZoom,
  customColorScale,
  events = EMPTY_EVENTS,
  onEventClick,
  panelKey,
}: HeatMapChartProps) {
  const chartApiRef = useRef<ChartApi>();
  const gridRect = useEChartsGridCoordinates(chartApiRef);
  const { chartsTheme } = useChartsContext();
  const hasEvents = events.length > 0;
  const showEvents = hasEvents && gridRect && height >= MIN_CHART_HEIGHT + EVENT_TIMELINE_HEIGHT;
  const chartAreaHeight = Math.max(0, height - (showEvents ? EVENT_TIMELINE_HEIGHT : 0));

  const gridOption = useMemo(() => {
    // Need to leave a little extra space to avoid clipping the left of some characters
    // in the y axis labels and some of the chart on the right.
    const gridXBase = 0;
    // Matches the line height of the y axis label.
    const labelWidth = 20;
    // Padding between the right side of the label and the left side of the chart.
    const labelChartPadding = chartsTheme.yAxis.label.chartPadding;

    const gridLeft = yAxis.show && yAxis.label ? gridXBase + labelWidth + labelChartPadding : gridXBase;
    return {
      left: gridLeft,
      right: legendOptions.show && legendOptions.position === 'Right' ? PADDING.right.legend : PADDING.right.default,
      bottom:
        legendOptions.show && legendOptions.position === 'Bottom' ? PADDING.bottom.legend : PADDING.bottom.default,
      containLabel: true,
    };
  }, [chartsTheme.yAxis.label.chartPadding, yAxis, legendOptions.show, legendOptions.position]);

  const { throttledWidth } = useThrottledResizing(
    { width, height },
    {
      // We use a moderate throttle for this because it primarily impacts the time-based
      // x-axis reorganizing, which isn't pixel-perfect, so you don't notice choppiness
      // if it runs a little slower.
      throttleDimensionsMs: 100,
    }
  );

  const minInterval = useMemo(() => {
    // Adjust x-axis minInterval based on chart width so labels are not squished together
    // https://echarts.apache.org/en/tutorial.html#Responsive%20Mobile-End
    const timeRangeMs = data.timeRange.endMs.valueOf() - data.timeRange.startMs.valueOf();
    return getXAxisMinInterval(throttledWidth, timeRangeMs);
  }, [data, throttledWidth]);

  const xAxisOption = useMemo(() => {
    const startMs = data.timeRange.startMs.valueOf();
    const endMs = data.timeRange.endMs.valueOf();
    return {
      type: 'time',
      splitLine: { show: false },
      min: timeZone === 'local' ? startMs : utcToZonedTime(startMs, timeZone),
      max: timeZone === 'local' ? endMs : utcToZonedTime(endMs, timeZone),
      minInterval,
      axisLabel: {
        hideOverlap: true,
        formatter: getFormattedAxisLabel(endMs - startMs),
      },
    };
  }, [minInterval, data, timeZone]);

  const unit = useMemo(() => {
    return merge({}, DEFAULT_UNIT, yAxis.unit);
  }, [yAxis.unit]);

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const yAxisOption = useMemo(() => {
    const bucketUpperBoundaries: number[] = new Array(data.buckets.length);
    for (let i = 0; i < data.buckets.length; i++) {
      bucketUpperBoundaries[i] = data.buckets[i]?.upper ?? 0;
    }

    return {
      show: yAxis.show,
      type: 'category',
      splitLine: { show: false },
      data: bucketUpperBoundaries,
      axisLabel: {
        formatter: (value: number) => {
          return formatBucketBoundary(value, unit);
        },
      },
    };
  }, [data, unit, yAxis?.show]);

  const colorOptions: HeatmapCanvasColorOptions = useMemo(() => {
    if (customColorScale) {
      return {
        ...initColorOptions,
        customColorScale,
      };
    }

    return initColorOptions;
  }, [initColorOptions, customColorScale]);

  const visualMapOption = useMemo(() => {
    const min = Math.max(0, colorOptions.min ?? 0); // min value must be >=0
    const max = colorOptions.max ?? data.maxValue;
    let colorScale = colorOptions.customColorScale?.colorScale ?? ColorScales[colorOptions.scale] ?? ColorScales.turbo;

    if (colorOptions.invertScale) colorScale = [...colorScale].reverse();

    // if value range is 0->0 restrict color scale to a single entry
    if (min === max) colorScale = colorScale.slice(0, 1);

    return {
      min,
      max,
      show: legendOptions.show,
      itemWidth: 10,
      orient: legendOptions.position === 'Right' ? 'vertical' : 'horizontal',
      align: legendOptions.position === 'Right' ? 'top' : 'left',
      textGap: 4,
      hoverLink: false,
      top: legendOptions.position === 'Right' ? 0 : undefined,
      right: legendOptions.position === 'Right' ? 0 : undefined,
      bottom: legendOptions.position === 'Bottom' ? 0 : undefined,
      left: legendOptions.position === 'Bottom' ? 50 : undefined,
      text: [
        formatValue(max, { kind: 'Decimal', abbreviate: true }),
        formatValue(min, { kind: 'Decimal', abbreviate: true }),
      ],
      textStyle: { fontSize: 11, color: 'inherit' },
      inRange: { color: scaleToRgbString(colorScale) },
      padding: 5,
    };
  }, [data, colorOptions, legendOptions]);

  const option: EChartsCoreOption = useMemo(() => {
    const noData = data.timeSlices.length === 0;
    const baseOptions = noData ? chartsTheme.noDataOption : {};

    return {
      ...baseOptions,
      ...CONSTANT_OPTIONS,
      grid: gridOption,
      xAxis: xAxisOption,
      yAxis: !noData
        ? yAxisOption
        : {
            ...yAxisOption,
            ...NO_DATA_YAXIS_OPTIONS,
          },
      series: [],
      visualMap: visualMapOption,
    };
  }, [gridOption, xAxisOption, yAxisOption, data, visualMapOption, chartsTheme]);

  const absoluteTimeRange: AbsoluteTimeRange = useMemo(
    () => ({
      start: new Date(data.timeRange.startMs),
      end: new Date(data.timeRange.endMs),
    }),
    [data.timeRange.startMs, data.timeRange.endMs]
  );

  return (
    <Box sx={{ width, height, overflow: 'auto' }}>
      {showEvents && (
        <Box sx={{ height: `${EVENT_TIMELINE_HEIGHT}px`, width }}>
          <Box sx={{ paddingLeft: `${gridRect.x}px` }}>
            <EventTimeline
              events={events}
              timeRange={absoluteTimeRange}
              width={gridRect.width}
              height={EVENT_TIMELINE_HEIGHT}
              onEventClick={onEventClick}
              parentPanelKey={panelKey ?? 'heatmap-chart'}
            />
          </Box>
        </Box>
      )}

      <Box sx={{ width, height: chartAreaHeight, position: 'relative' }}>
        {yAxis.show && yAxis.label && (
          <Box sx={{ position: 'absolute', top: 0, left: 0, height: chartAreaHeight, width: '100%' }}>
            <YAxisLabel name={yAxis?.label} height={chartAreaHeight} />
          </Box>
        )}
        <EChart
          chartApiRef={chartApiRef}
          width={width}
          height={chartAreaHeight}
          option={option}
          theme={chartsTheme.echartsTheme}
        />
        {gridRect && data.timeSlices.length > 0 && (
          <>
            <Box position="absolute" top={gridRect.y} left={gridRect.x} width={gridRect.width} height={gridRect.height}>
              <HeatMapCanvas width={gridRect.width} height={gridRect.height} data={data} colorOptions={colorOptions} />
            </Box>
            <Box position="absolute" top={gridRect.y} left={gridRect.x} width={gridRect.width} height={gridRect.height}>
              <HeatMapInteractivePane
                width={gridRect.width}
                height={gridRect.height}
                data={data}
                unit={unit}
                onDataZoom={onDataZoom}
              />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
